<template>
  <section class="encounter-info-panel">
    <div class="encounter-info-ledger">
      <div class="encounter-info-row">
        <span class="encounter-info-label">HMO Agent(s)</span>
        <span class="encounter-info-value">{{ agents.length ? agents.join(", ") : "Not recorded" }}</span>
      </div>

      <div class="encounter-info-row">
        <span class="encounter-info-label">Encounter Created</span>
        <span class="encounter-info-value">{{ formatDateTime(encounter?.created_at) }}</span>
      </div>

      <div class="encounter-info-row">
        <span class="encounter-info-label">Visit Started</span>
        <span class="encounter-info-value">{{ formatDate(encounter?.encounter_date) }}</span>
      </div>

      <div class="encounter-info-row">
        <span class="encounter-info-label">Visit Ended</span>
        <span class="encounter-info-value">{{ formatDate(encounter?.encounter_date_end) }}</span>
      </div>

      <div class="encounter-info-row">
        <span class="encounter-info-label">Auth Request Time(s)</span>
        <span class="encounter-info-value">
          <template v-if="authRequestTimes.length">
            <span v-for="time in authRequestTimes" :key="`request-${time}`" class="encounter-info-line">
              {{ formatDateTime(time) }}
            </span>
          </template>
          <template v-else>Not recorded</template>
        </span>
      </div>

      <div class="encounter-info-row" v-if="showAuthResponseInfo">
        <span class="encounter-info-label">Auth Approval Response(s)</span>
        <span class="encounter-info-value">
          <template v-if="authResponseTimes.length">
            <span v-for="time in authResponseTimes" :key="`response-${time}`" class="encounter-info-line">
              {{ formatDateTime(time) }}
            </span>
          </template>
          <template v-else>Not recorded</template>
        </span>
      </div>

      <div class="encounter-info-row" v-if="showAuthResponseInfo">
        <span class="encounter-info-label">Auth Response Time</span>
        <span class="encounter-info-value">
          <template v-if="authResponseDurations.length">
            <span
              v-for="duration in authResponseDurations"
              :key="duration.key"
              class="encounter-info-line"
            >
              {{ duration.label }}
            </span>
          </template>
          <template v-else>Not recorded</template>
        </span>
      </div>

      <div v-for="row in totalRows" :key="row.id" class="encounter-info-row">
        <span class="encounter-info-label">{{ row.title }}</span>
        <span class="encounter-info-value">
          {{ row.count }} {{ row.count === 1 ? "item" : "items" }}, &#8358;{{ row.amount }}
        </span>
      </div>
    </div>
  </section>
</template>

<script>
import { computed } from "vue";

export default {
  name: "EncounterInfoComponent",
  props: {
    encounter: {
      type: Object,
      default: null,
    },
    mode: {
      type: String,
      default: "auth",
    },
    clusterStatus: {
      type: [String, Number],
      default: null,
    },
  },
  setup(props) {
    const claims = computed(() => {
      return Array.isArray(props.encounter?.claims) ? props.encounter.claims : [];
    });

    const cleanValue = (value) => {
      if (value === null || value === undefined) {
        return "";
      }

      return String(value).trim();
    };

    const uniqueValues = (values) => {
      const seen = new Set();

      return values.reduce((items, value) => {
        const clean = cleanValue(value);

        if (!clean || seen.has(clean)) {
          return items;
        }

        seen.add(clean);
        items.push(clean);
        return items;
      }, []);
    };

    const effectiveClusterStatus = computed(() => {
      return cleanValue(props.clusterStatus)
        || cleanValue(props.encounter?.cluster?.status)
        || cleanValue(props.encounter?.claim_cluster?.status)
        || cleanValue(claims.value[0]?.cluster?.status);
    });

    const showAuthResponseInfo = computed(() => {
      const status = effectiveClusterStatus.value.toLowerCase();
      return !status || status === "q";
    });

    const agents = computed(() => {
      return uniqueValues(claims.value.map((claim) => claim?.slug));
    });

    const authClaims = computed(() => {
      return claims.value.filter((claim) => ["z", "y"].includes(claim?.authorization));
    });

    const reviewedAuthClaims = computed(() => {
      return authClaims.value.filter((claim) => {
        return (claim?.authorization === "y" && claim?.status === "a")
          || (claim?.authorization === "z" && claim?.status === "d");
      });
    });

    const authRequestTimes = computed(() => {
      return uniqueValues(authClaims.value.map((claim) => claim?.created_at));
    });

    const authResponseTimes = computed(() => {
      return uniqueValues(reviewedAuthClaims.value.map((claim) => claim?.updated_at));
    });

    const parseDate = (value) => {
      const clean = cleanValue(value);

      if (!clean) {
        return null;
      }

      const normalized = clean.includes(" ") ? clean.replace(" ", "T") : clean;
      const date = new Date(normalized);

      if (Number.isNaN(date.getTime())) {
        return null;
      }

      return date;
    };

    const formatDateTime = (value) => {
      const date = parseDate(value);

      if (!date) {
        return "Not recorded";
      }

      return date.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    };

    const formatDate = (value) => {
      const clean = cleanValue(value);

      if (!clean) {
        return "Not recorded";
      }

      const dateParts = clean.match(/^(\d{4})-(\d{2})-(\d{2})/);
      const date = dateParts
        ? new Date(Number(dateParts[1]), Number(dateParts[2]) - 1, Number(dateParts[3]))
        : parseDate(clean);

      if (!date || Number.isNaN(date.getTime())) {
        return "Not recorded";
      }

      return date.toLocaleDateString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    const formatDuration = (startValue, endValue) => {
      const start = parseDate(startValue);
      const end = parseDate(endValue);

      if (!start || !end || end.getTime() < start.getTime()) {
        return "Not recorded";
      }

      const totalMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));

      if (totalMinutes < 1) {
        return "Under 1 min";
      }

      const days = Math.floor(totalMinutes / 1440);
      const hours = Math.floor((totalMinutes % 1440) / 60);
      const minutes = totalMinutes % 60;
      const parts = [];

      if (days) {
        parts.push(`${days}d`);
      }

      if (hours) {
        parts.push(`${hours}h`);
      }

      if (minutes || !parts.length) {
        parts.push(`${minutes}m`);
      }

      return parts.join(" ");
    };

    const authResponseDurations = computed(() => {
      const seen = new Set();

      return reviewedAuthClaims.value.reduce((items, claim) => {
        const createdAt = cleanValue(claim?.created_at);
        const updatedAt = cleanValue(claim?.updated_at);
        const key = `${createdAt}-${updatedAt}`;

        if (!createdAt || !updatedAt || seen.has(key)) {
          return items;
        }

        seen.add(key);
        items.push({
          key,
          label: formatDuration(createdAt, updatedAt),
        });

        return items;
      }, []);
    });

    const priceValue = (value) => {
      const clean = cleanValue(value).replace(/[^\d.-]/g, "");
      const price = Number(clean);

      return Number.isNaN(price) ? 0 : price;
    };

    const hasApprovedAmount = (claim) => {
      return claim?.approved_price !== null
        && claim?.approved_price !== undefined
        && claim?.approved_price !== "";
    };

    const approvedAmount = (claim) => {
      return hasApprovedAmount(claim) ? priceValue(claim?.approved_price) : priceValue(claim?.sent_price);
    };

    const money = (value) => {
      return Number(value || 0)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const summarize = (items, amountResolver) => {
      const amount = items.reduce((total, claim) => {
        return total + amountResolver(claim);
      }, 0);

      return {
        count: items.length,
        amount: money(amount),
      };
    };

    const totalRequest = computed(() => {
      return summarize(authClaims.value, (claim) => priceValue(claim?.sent_price));
    });

    const approvedClaims = computed(() => {
      return authClaims.value.filter((claim) => claim?.authorization === "y" && claim?.status === "a");
    });

    const totalApproved = computed(() => {
      return summarize(approvedClaims.value, approvedAmount);
    });

    const adjustedClaims = computed(() => {
      return approvedClaims.value.filter((claim) => {
        return priceValue(claim?.sent_price) !== approvedAmount(claim)
          || Number(claim?.sent_qty || 0) !== Number(claim?.approved_qty || claim?.sent_qty || 0);
      });
    });

    const totalAdjusted = computed(() => {
      return summarize(adjustedClaims.value, approvedAmount);
    });

    const declinedClaims = computed(() => {
      return authClaims.value.filter((claim) => claim?.authorization === "z" && claim?.status === "d");
    });

    const totalDeclined = computed(() => {
      return summarize(declinedClaims.value, (claim) => priceValue(claim?.sent_price));
    });

    const totalRows = computed(() => {
      return [
        { id: "request", title: "Total Request", ...totalRequest.value },
        { id: "approved", title: "Total Approved", ...totalApproved.value },
        { id: "adjusted", title: "Total Adjusted", ...totalAdjusted.value },
        { id: "declined", title: "Total Declined", ...totalDeclined.value },
      ];
    });

    return {
      agents,
      authRequestTimes,
      authResponseTimes,
      authResponseDurations,
      showAuthResponseInfo,
      totalRows,
      formatDate,
      formatDateTime,
    };
  },
};
</script>

<style scoped>
.encounter-info-panel {
  background: #ffffff;
  color: #263238;
  padding: 28px 16px;
}

.encounter-info-ledger {
  margin: 0 auto;
  max-width: 920px;
  position: relative;
}

.encounter-info-ledger::before {
  background: #c4d3d9;
  bottom: 0;
  content: "";
  left: 50%;
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  width: 1px;
}

.encounter-info-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  min-height: 42px;
  position: relative;
}

.encounter-info-label,
.encounter-info-value {
  font-size: 0.95rem;
  line-height: 1.45;
  padding: 10px 26px;
}

.encounter-info-label {
  color: #607d8b;
  font-weight: 700;
  text-align: right;
}

.encounter-info-value {
  color: #263238;
  font-weight: 500;
  text-align: left;
}

.encounter-info-line {
  display: block;
}

.encounter-info-line + .encounter-info-line {
  margin-top: 4px;
}

@media only screen and (max-width: 700px) {
  .encounter-info-panel {
    padding: 18px 10px;
  }

  .encounter-info-label,
  .encounter-info-value {
    font-size: 0.88rem;
    padding-left: 14px;
    padding-right: 14px;
  }
}
</style>
